import {
  getStationIdForLookup,
  normalizeProvider,
} from "../services/stationLogos";
import type { Station } from "../types";

/**
 * Known broken image URL patterns that should be filtered out
 * These return 404, SSL errors, or other issues
 */
const BROKEN_IMAGE_PATTERNS = [
  // Broken local paths
  "bbc-world-service.png", // Catches both local and Supabase versions
  "/img/stations/bbc-world-service.png",
  // Supabase storage - broken files (catches URL-encoded versions too)
  "rix-fm.png", // More aggressive - catches all rix-fm.png references
  "station-logos/logos/rix-fm",
  "station-logos/logos/bbc-world-service",
  // External CDNs with issues
  "chefpablos.com",
  "static-cdn.sr.se",
  "sounds.files.bbci.co.uk",
  "sverigesradio.se/images", // Old SR image CDN
  // Specific problematic UUIDs
  "d74bc0d7-5fa5-429c-95cd-1aa698244b2a",
  // Broken API paths
  "/api/logo_1400x1400",
];

/**
 * Sanitize image URL - returns empty string for known broken URLs
 */
export const sanitizeImageUrl = (url: string | undefined | null): string => {
  if (!url) return "";

  // Check against all broken patterns
  const urlLower = url.toLowerCase();
  for (const pattern of BROKEN_IMAGE_PATTERNS) {
    if (urlLower.includes(pattern.toLowerCase())) {
      return ""; // Return empty to trigger fallback
    }
  }

  return url;
};

/**
 * Legacy proxy function - now redirects to SNAPI
 *
 * @deprecated Use getStationLogoUrl for stations with provider/id
 * For generic URLs (like podcast episode images), this still works via SNAPI
 *
 * @param url - Image URL to proxy
 * @returns SNAPI proxy URL
 */
export const getProxyUrl = (url: string | undefined | null): string => {
  // First sanitize to filter broken URLs
  const sanitized = sanitizeImageUrl(url);
  if (!sanitized) return "";

  if (sanitized.startsWith("/") || sanitized.startsWith("data:"))
    return sanitized;

  // Known reliable HTTPS podcast hosts - serve directly
  if (
    sanitized.includes("mzstatic.com") ||
    sanitized.includes("simplecastcdn.com") ||
    sanitized.includes("libsyn.com") ||
    sanitized.includes("flightcast.com") ||
    sanitized.includes("omnycontent.com") ||
    sanitized.includes("imgix.net") ||
    sanitized.includes("dancarlin.com")
  ) {
    return sanitized;
  }

  // Route other external images through SNAPI for caching/security
  return `/api/snapi?url=${encodeURIComponent(sanitized)}`;
};

/**
 * Get station logo URL via SNAPI v1 proxy
 *
 * Routes station images through SNAPI endpoint for:
 * - HD logo serving from Supabase (when available)
 * - Mixed content protection (http -> https)
 * - Caching and performance
 * - Graceful fallback handling
 *
 * @param station - Station object with id, provider, and logo info
 * @returns SNAPI URL with stationId/provider for HD lookup, fallback to origin
 *
 * @example
 * ```ts
 * // Will try HD logo, fallback to station.logoUrl
 * const url = getStationLogoUrl(station);
 * <img src={url} alt={station.name} />
 * ```
 */
export const getStationLogoUrl = (
  station:
    | Station
    | { id: string; provider: string; logoUrl?: string; favicon?: string },
  options: { width?: number; quality?: number } = {},
): string => {
  // Get origin URL (fallback) and sanitize it
  const rawOriginUrl =
    station.logoUrl || (station as { favicon?: string }).favicon;
  const originUrl = sanitizeImageUrl(rawOriginUrl);

  // Don't proxy data URIs
  if (originUrl?.startsWith("data:")) {
    return originUrl;
  }

  // For relative URLs (local images), we return them directly.
  if (originUrl?.startsWith("/")) {
    return originUrl;
  }

  // Build SNAPI v1 URL with station identifiers for HD lookup
  const params = new URLSearchParams();

  // Add origin URL
  if (originUrl) {
    params.set("url", originUrl);
  }

  // Add optimization params
  if (options.width) {
    params.set("w", options.width.toString());
  }
  if (options.quality) {
    params.set("q", options.quality.toString());
  }

  // Add station identifiers for HD logo lookup (v1)
  try {
    const stationId = getStationIdForLookup(station);
    const provider = normalizeProvider(station.provider);

    if (stationId && provider) {
      params.set("stationId", stationId);
      params.set("provider", provider);
    }
  } catch (error) {
    // If we can't determine station ID, just use origin URL (v0 behavior)
    console.warn(
      "[getStationLogoUrl] Could not determine station identifiers:",
      error,
    );
  }

  // If we have neither an origin URL nor a stationId/provider pair, return fallback
  if (
    !params.has("url") &&
    (!params.has("stationId") || !params.has("provider"))
  ) {
    return "/images/station-fallback.svg";
  }

  return `/api/snapi?${params.toString()}`;
};
